import { FirebaseCachedImage } from "@components/FirebaseCachedImage";
import { Modal } from "@components/Modal";
import { useTranslatedKey } from "@helpers/useTranslatedKey";
import { useTranslation } from "@helpers/useTranslation";
import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { decodePin } from "src/helpers/codec";
import { getBeginCopy, getLocaleFlag, useLocales } from "src/helpers/locales";
import { useBranding } from "src/helpers/useBranding";
import { useCountry } from "src/helpers/useCountry";
import { useEvent, useEventId } from "src/helpers/useEvent";
import { useEventNavigate } from "src/helpers/useEventNavigate";
import { useFirebaseCachedImageUrl } from "src/helpers/useFirebaseCachedImageUrl";
import { useLocalePreference } from "src/helpers/useLocalePreference";
import { usePersistedState } from "src/helpers/usePersistedState";

export const Landing = () => {
  const navigate = useEventNavigate();
  const { t, i18n } = useTranslation();
  const [appData, setAppData] = usePersistedState();
  const { getLocale, setLocale } = useLocalePreference();
  const { country } = useCountry();

  useEffect(() => {
    // Only auto-detect if no locale has been explicitly stored yet
    const currentLocale = getLocale();
    const hasStoredLocale = (() => {
      try {
        const eventId = window.location.pathname.split("/").filter(Boolean)[0];
        return eventId && window.localStorage.getItem(`camdeed-locale-${eventId}`) !== null;
      } catch {
        return false;
      }
    })();

    if (!hasStoredLocale && country) {
      const countryToLocale = {
        LT: "lt",
        US: "en",
        GB: "en",
      };
      const detectedLocale = (countryToLocale[country] || "en") as AppLocale;
      if (detectedLocale !== currentLocale) {
        setLocale(detectedLocale);
      }
    }
  }, [country]);

  const location = useLocation();
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const pinEncoded = params.get("p");
    if (pinEncoded) {
      const pin = decodePin(pinEncoded);
      setAppData({ ...appData, pin });
    }
  }, [location.search]);

  const [showConsent, setShowConsent] = useState(false);
  const [isEventDay, setIsEventDay] = useState(false);

  const [event, loading] = useEvent();
  const { name, type, date, landingConfig } = event || {};

  // Add these new state variables
  const [countdown, setCountdown] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
  });

  useEffect(() => {
    if (date) {
      // Create date at midnight in the client's local timezone
      const createLocalMidnight = (dateString: string) => {
        const date = new Date(dateString);
        return new Date(date.getFullYear(), date.getMonth(), date.getDate());
      };

      const eventDate = createLocalMidnight(date);

      // Initial calculation
      const calculateTimeLeft = () => {
        const now = new Date();
        const difference = eventDate.getTime() - now.getTime();

        // Event day or past
        if (difference <= 0) {
          setIsEventDay(true);
          return { days: 0, hours: 0, minutes: 0, seconds: 0 };
        }

        setIsEventDay(false);

        const days = Math.floor(difference / (1000 * 60 * 60 * 24));
        const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((difference % (1000 * 60)) / 1000);

        return { days, hours, minutes, seconds };
      };

      setCountdown(calculateTimeLeft());

      // Update countdown every second
      const timer = setInterval(() => {
        const timeLeft = calculateTimeLeft();
        setCountdown(timeLeft);

        if (timeLeft.days <= 0 && timeLeft.hours <= 0 && timeLeft.minutes <= 0 && timeLeft.seconds <= 0) {
          clearInterval(timer);
        }
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [date]);

  const eventId = useEventId();
  const imageUrl = useFirebaseCachedImageUrl(`events/${eventId}/background_light`, `events/${eventId}/background.jpg`);
  const defaultUrl = type === "wedding" ? "/bg-wedding.webp" : "/bg-conference.webp";
  const backgroundImage = imageUrl ? `url(${imageUrl})` : `url(${defaultUrl})`;

  const locales = useLocales();

  const { brandName, homepage, logoUrl } = useBranding();

  const hasCustomBranding = event?.hasBranding;

  // Handle landing config
  const { getTranslatedValue } = useTranslatedKey();
  const isLandingEnabled = landingConfig?.enabled ?? false;
  const showCountdown = landingConfig?.showCountdown ?? false;
  const invitationCopy = getTranslatedValue(landingConfig, "invitationCopy") ?? "";
  const showDate = landingConfig?.showDate ?? true;
  const showLocation = landingConfig?.showLocation ?? false;

  // const isPromo = !!event?.isPromo;

  const goToIntro = () => {
    // const needsConsent = isPromo;
    const needsConsent = false;
    const hasConsent = appData.consent;
    if (needsConsent && !hasConsent) {
      setShowConsent(true);
      return;
    }

    navigate("/intro");
  };

  const consentClose = () => {
    setAppData({ ...appData, consent: true });
    setShowConsent(false);
    navigate("/intro");
  };

  const formatDateForLocale = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const options: Intl.DateTimeFormatOptions = {
      year: "numeric",
      month: "long",
      day: "numeric",
    };
    return date.toLocaleDateString(i18n.language, options);
  };

  // Create Google Maps URL if coordinates are provided
  // const mapsUrl = useMemo(() => {
  //   if (!locationCoords) return null;
  //   return `https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2500!2d${locationCoords.split(",")[1]}!3d${
  //     locationCoords.split(",")[0]
  //   }!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zM!5e0!3m2!1sen!2sus!4v1650000000000!5m2!1sen!2sus`;
  // }, [locationCoords]);

  // Determine whether to show pre-event content or direct entry buttons
  const shouldShowPreEventContent = isLandingEnabled && !isEventDay;
  const hasPreEventContent = invitationCopy || showCountdown || showDate || showLocation;

  // Non-existing event, redirect to landing page
  if (!event && !loading) window.location.replace(homepage);
  if (!event) return null;

  // We use the historical "rockit" event in promotional material/landing video, so best to direct people out of this
  const isRockit = eventId === "rockit";
  if (isRockit) window.location.replace(homepage);

  const consentTitle = t("admin:consentTitle");
  const consentText = t("admin:consentText");

  const isTrenkturas = eventId === "coastlinetrek";
  const isElgamaFiestaLatina = eventId === "elgamafiestalatina";

  return (
    <div
      style={{ backgroundImage }}
      className={`flex min-h-screen justify-center overflow-auto bg-gray-800 bg-cover bg-center bg-blend-overlay`}
    >
      <Modal
        show={showConsent}
        onCancel={consentClose}
        hideX
        closeCopy={t("admin:consentCloseCopy")}
        size="xl"
        preventClose
        className="px-2 pt-24"
      >
        <h3 className="text-lg font-bold">{consentTitle}</h3>
        <p className="pre-wrap whitespace-pre-wrap py-4">{consentText}</p>
      </Modal>
      <div className="container flex justify-center">
        <main className="fadein flex w-full max-w-md flex-1 flex-col items-center justify-between pt-24 pb-8">
          <h1 className="mb-4 px-2 text-center text-4xl font-bold uppercase leading-normal text-white sm:text-5xl">
            {isElgamaFiestaLatina ? (
              <div className="flex justify-center">
                <img src="/elgamafiestalatinalogo.png" alt="Elgama Fiesta Latina" className="w-auto max-w-xs" />
              </div>
            ) : (
              <div>{name}</div>
            )}
          </h1>

          {event.subTitle && (
            <div className="text px-6 text-center text-slate-200">
              <p>{event.subTitle}</p>
            </div>
          )}

          {shouldShowPreEventContent && (
            <div className={`w-full px-8`}>
              {hasPreEventContent && (
                <div className="rounded-2xl bg-primary bg-opacity-80 p-4 px-4 text-center">
                  {invitationCopy && (
                    <div className="mt-4 rounded-lg p-3 text-center text-white">
                      <h2 className="text-lg font-semibold">{t("admin:invitationCopy")}</h2>
                      <p className="text-md mb-4 text-slate-200">{invitationCopy}</p>
                    </div>
                  )}
                  {showCountdown && (
                    <div className="mt-4 rounded-lg p-3 text-center">
                      <h2 className="text-lg text-white">{t("admin:countdownTitle")}</h2>
                      <div className="mt-2 flex justify-center gap-2 text-slate-200">
                        <div className="flex flex-col items-center">
                          <div className="text-xl font-bold">{countdown.days}</div>
                          <div className="text-sm">{t("admin:days")}</div>
                        </div>
                        <div className="text-xl font-bold">:</div>
                        <div className="flex flex-col items-center">
                          <div className="text-xl font-bold">{String(countdown.hours).padStart(2, "0")}</div>
                          <div className="text-sm">{t("admin:hours")}</div>
                        </div>
                        <div className="text-xl font-bold">:</div>
                        <div className="flex flex-col items-center">
                          <div className="text-xl font-bold">{String(countdown.minutes).padStart(2, "0")}</div>
                          <div className="text-sm">{t("admin:minutes")}</div>
                        </div>
                        <div className="text-xl font-bold">:</div>
                        <div className="flex flex-col items-center">
                          <div className="text-xl font-bold">{String(countdown.seconds).padStart(2, "0")}</div>
                          <div className="text-sm">{t("admin:seconds")}</div>
                        </div>
                      </div>
                    </div>
                  )}

                  {showDate && date && (
                    <div className="rounded-lg p-3 text-center text-white">
                      <h2 className="text-lg font-semibold">{t("admin:eventDateLabel")}</h2>
                      <div className="text-md mb-2 text-slate-200">{formatDateForLocale(date)}</div>
                    </div>
                  )}

                  {/* {showLocation && locationName && (
                  <div className="mt-4 mb-4 rounded-lg p-3 text-center text-white">
                    <h2 className="mb-1 text-lg">{t("admin:location")}</h2>
                    <div className="text-md mb-8 text-slate-200">{locationName}</div>

                    {locationCoords && mapsUrl && (
                      <div className="mt-2">
                        <iframe
                          title="Event Location"
                          src={mapsUrl}
                          width="100%"
                          height="150"
                          style={{ border: 0 }}
                          allowFullScreen
                          loading="lazy"
                          referrerPolicy="no-referrer-when-downgrade"
                          className="rounded-md"
                        ></iframe>
                      </div>
                    )}
                  </div>
                )} */}
                </div>
              )}
            </div>
          )}
          {!shouldShowPreEventContent && (
            <div className="mt-8 flex w-full max-w-xs flex-col justify-center align-bottom">
              {locales.map((locale) => (
                <button
                  key={locale}
                  className="btn-outline btn mb-7 border border-base-300 bg-base-200 bg-opacity-80 uppercase text-neutral"
                  onClick={(e) => {
                    e.preventDefault();
                    setLocale(locale);
                    goToIntro();
                  }}
                >
                  <span className="-ml-3 mr-4 text-xl">{getLocaleFlag(locale)}</span>
                  {getBeginCopy(locale)}
                </button>
              ))}
            </div>
          )}

          {isTrenkturas && (
            <div className="flex items-center justify-center">
              <img src="/trenkturas-vector.svg" alt="Trenkturas" className="h-20 w-auto" />
            </div>
          )}

          <div className="bottom-0 mt-8 max-w-lg text-center">
            <div>
              {isElgamaFiestaLatina && (
                <img
                  src="/elgama-logo.png"
                  alt="Elgama Fiesta Latina"
                  className="mx-auto mb-12 w-auto max-w-xs px-12"
                />
              )}
            </div>
            <div>
              <>
                {hasCustomBranding ? (
                  <div className="flex h-36 items-center justify-center">
                    <FirebaseCachedImage
                      uri={`events/${eventId}/branding`}
                      alt={brandName}
                      className="max-h-full max-w-[12rem] object-contain"
                    />
                  </div>
                ) : (
                  <div className="h-20 opacity-70">
                    <a href={homepage}>
                      <img src={logoUrl} alt={brandName} className="mx-auto w-32" />
                    </a>
                  </div>
                )}
              </>

              <Link to={`/login`}>
                <div className="mt-8 mb-2 p-2 text-center text-sm text-slate-200 underline">
                  <span>Admin Login</span>
                </div>
              </Link>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};
